import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, FormControlLabel, Switch, Tooltip, Avatar, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Add, Delete, Edit, Visibility, CalendarMonth, HolidayVillage } from '@mui/icons-material';
import { holidaysAPI, employeeHolidaysAPI } from '../services/api';
import Swal from 'sweetalert2';

const Holidays = () => {
  const [holidays, setHolidays] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [holidaySelections, setHolidaySelections] = useState([]);
  const [formData, setFormData] = useState({ holiday_name: '', holiday_date: '', description: '', is_optional: false, is_global: true, year: new Date().getFullYear() });
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => { loadHolidays(); }, []);

  const loadHolidays = async () => {
    const response = await holidaysAPI.getAll({ year: new Date().getFullYear() });
    if (response.data.success) setHolidays(response.data.data.holidays);
  };

  const handleSave = async () => {
    try {
      if (!formData.holiday_name.trim()) {
        await Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'Holiday name is required',
          background: 'rgb(10, 25, 41)',
          color: '#D4AF37',
          confirmButtonColor: '#D4AF37',
        });
        return;
      }

      if (!formData.holiday_date) {
        await Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'Holiday date is required',
          background: 'rgb(10, 25, 41)',
          color: '#D4AF37',
          confirmButtonColor: '#D4AF37',
        });
        return;
      }

      if (!formData.year) {
        await Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'Year is required',
          background: 'rgb(10, 25, 41)',
          color: '#D4AF37',
          confirmButtonColor: '#D4AF37',
        });
        return;
      }

      if (!isEditMode) {
        const selectedDate = new Date(formData.holiday_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (selectedDate < today) {
          await Swal.fire({
            icon: 'warning',
            title: 'Date Validation',
            text: 'Holiday date cannot be in the past. Please select a future date.',
            background: 'rgb(10, 25, 41)',
            color: '#D4AF37',
            confirmButtonColor: '#D4AF37',
          });
          return;
        }
      }

      if (isEditMode) {
        await holidaysAPI.update(selectedHoliday.id, {
          holiday_name: formData.holiday_name,
          holiday_date: formData.holiday_date,
          description: formData.description,
          is_optional: formData.is_optional,
          is_global: formData.is_global,
          category: formData.category || '',
          year: formData.year
        });
        await Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Holiday updated successfully',
          timer: 2000,
          showConfirmButton: false,
          background: 'rgb(10, 25, 41)',
          color: '#D4AF37',
        });
      } else {
        await holidaysAPI.create({
          ...formData,
          category: formData.category || null
        });
        await Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Holiday created successfully',
          timer: 2000,
          showConfirmButton: false,
          background: 'rgb(10, 25, 41)',
          color: '#D4AF37',
        });
      }

        setShowDialog(false);
        setFormData({ holiday_name: '', holiday_date: '', description: '', is_optional: false, is_global: true, year: new Date().getFullYear(), category: '' });
        setIsEditMode(false);
        setSelectedHoliday(null);
        loadHolidays();
    } catch (error) {
      console.error('Error saving holiday:', error);

      let errorMessage = 'Failed to save holiday';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid data provided. Please check all fields.';
      } else if (error.response?.status === 401) {
        errorMessage = 'You are not authorized to create holidays.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error occurred. Please try again later.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      await Swal.fire({
        icon: 'error',
        title: 'Error Saving Holiday',
        text: errorMessage,
        background: 'rgb(10, 25, 41)',
        color: '#D4AF37',
        confirmButtonColor: '#D4AF37',
      });
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Holiday',
      text: 'Are you sure you want to delete this holiday?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d32f2f',
      background: 'rgb(10, 25, 41)',
      color: '#D4AF37',
      cancelButtonColor: '#666',
    });
    if (result.isConfirmed) {
      await holidaysAPI.delete(id);
      loadHolidays();
      await Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: 'Holiday has been deleted.',
        background: 'rgb(10, 25, 41)',
        color: '#D4AF37',
        confirmButtonColor: '#D4AF37',
        timer: 2000,
        showConfirmButton: false,
      });
    }
  };

  const handleView = async (holiday) => {
    setSelectedHoliday(holiday);
    if (holiday.is_optional) {
      try {
        const response = await employeeHolidaysAPI.getAll({ year: holiday.year });
        if (response.data.success) {
          const selections = response.data.data.filter(s => s.holiday_id === holiday.id);
          setHolidaySelections(selections);
        }
      } catch (error) {
        console.error('Error fetching holiday selections:', error);
        setHolidaySelections([]);
      }
    } else {
      setHolidaySelections([]);
    }
    setShowViewDialog(true);
  };

  const handleEdit = (holiday) => {
    setSelectedHoliday(holiday);
    setFormData({
      holiday_name: holiday.holiday_name,
      holiday_date: holiday.holiday_date,
      description: holiday.description || '',
      is_optional: holiday.is_optional,
      is_global: holiday.is_global,
      year: holiday.year,
      category: holiday.category || ''
    });
    setIsEditMode(true);
    setShowDialog(true);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <Box sx={{ bgcolor: '#0A1929', minHeight: '100vh', p: 3 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#D4AF37' }}>
            <HolidayVillage sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle' }} />
            Holiday Calendar
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<Add />} 
            onClick={() => {
              setFormData({ holiday_name: '', holiday_date: '', description: '', is_optional: false, is_global: true, year: new Date().getFullYear() });
              setIsEditMode(false);
              setSelectedHoliday(null);
              setShowDialog(true);
            }}
            sx={{ bgcolor: '#D4AF37', color: 'rgb(10, 25, 41)', '&:hover': { bgcolor: '#C19B2E' } }}
          >
            Add Holiday
          </Button>
        </Box>

        {/* Holidays Table */}
        <Card sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#2A3E24' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', fontSize: '0.9rem' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', fontSize: '0.9rem' }}>Day</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', fontSize: '0.9rem' }}>Holiday Name</TableCell>
        <TableCell sx={{ fontWeight: 600, color: '#D4AF37', fontSize: '0.9rem' }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', fontSize: '0.9rem' }}>Employee Choice</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', fontSize: '0.9rem' }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {holidays.map((h) => (
                    <TableRow key={h.id} hover sx={{ '&:hover': { bgcolor: 'rgba(212,175,55,0.05)' } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarMonth sx={{ fontSize: 16, color: '#D4AF37' }} />
                          <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                            {formatDate(h.holiday_date)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          size="small" 
                          label={h.day} 
                          sx={{ 
                            bgcolor: 'rgba(212,175,55,0.2)', 
                            color: '#D4AF37',
                            border: '1px solid #D4AF37',
                            fontWeight: 500
                          }} 
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                          {h.holiday_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {h.category ? (
                          <Chip 
                            size="small" 
                            label={`Cat ${h.category}`}
                            sx={{
                              bgcolor: h.category === 'A' ? 'rgba(33, 150, 243, 0.2)' : 
                                       h.category === 'B' ? 'rgba(76, 175, 80, 0.2)' : 
                                       'rgba(255, 152, 0, 0.2)',
                              color: h.category === 'A' ? '#2196F3' : 
                                      h.category === 'B' ? '#4CAF50' : 
                                      '#FF9800',
                              border: `1px solid ${h.category === 'A' ? '#2196F3' : 
                                                     h.category === 'B' ? '#4CAF50' : 
                                                     '#FF9800'}`
                            }} 
                          />
                        ) : (
                          <Typography variant="body2" sx={{ color: '#B4C4A8' }}>-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          size="small" 
                          label={h.is_optional ? 'Employee Choice' : 'Auto-applied'} 
                          sx={{
                            bgcolor: h.is_optional ? 'rgba(255, 152, 0, 0.2)' : 'rgba(33, 150, 243, 0.2)',
                            color: h.is_optional ? '#FFB74D' : '#90CAF9',
                            border: `1px solid ${h.is_optional ? '#FFB74D' : '#90CAF9'}`
                          }} 
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            onClick={() => handleView(h)} 
                            sx={{ color: '#90CAF9', mr: 1, '&:hover': { bgcolor: 'rgba(144, 202, 249, 0.1)' } }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Holiday">
                          <IconButton 
                            size="small" 
                            onClick={() => handleEdit(h)} 
                            sx={{ color: '#D4AF37', mr: 1, '&:hover': { bgcolor: 'rgba(212,175,55,0.1)' } }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Holiday">
                          <IconButton 
                            size="small" 
                            onClick={() => handleDelete(h.id)} 
                            sx={{ color: '#EF9A9A', '&:hover': { bgcolor: 'rgba(239, 83, 80, 0.1)' } }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {holidays.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: 'center', py: 6 }}>
                        <Typography sx={{ color: '#B4C4A8' }}>No holidays found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Add/Edit Holiday Dialog */}
        <Dialog 
          open={showDialog} 
          onClose={() => setShowDialog(false)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'rgb(10, 25, 41)',
              border: '2px solid #D4AF37',
              borderRadius: 3,
            }
          }}
        >
          <DialogTitle sx={{ color: '#D4AF37', fontWeight: 600, borderBottom: '1px solid rgba(212,175,55,0.3)' }}>
            {isEditMode ? 'Edit Holiday' : 'Add New Holiday'}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Holiday Name"
              value={formData.holiday_name}
              onChange={(e) => setFormData({ ...formData, holiday_name: e.target.value })}
              sx={{ mt: 2, mb: 2 }}
              required
              InputLabelProps={{ sx: { color: '#B4C4A8' } }}
              InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
            />
            <TextField
              fullWidth
              label="Date"
              type="date"
              value={formData.holiday_date}
              onChange={(e) => setFormData({ ...formData, holiday_date: e.target.value })}
              InputLabelProps={{ shrink: true, sx: { color: '#B4C4A8' } }}
              sx={{ mb: 2 }}
              required
              InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
            />
            <TextField 
              fullWidth 
              label="Description" 
              multiline 
              rows={2} 
              value={formData.description} 
              onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { color: '#B4C4A8' } }}
              InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ color: '#B4C4A8' }}>Category (Optional)</InputLabel>
              <Select
                value={formData.category || ''}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                sx={{ 
                  color: '#FFFFFF',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#C19B2E' }
                }}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="A">Category A - National Holidays (Min: 2, Max: 3)</MenuItem>
                <MenuItem value="B">Category B - Regional Festivals (Min: 2, Max: 4)</MenuItem>
                <MenuItem value="C">Category C - Religious Holidays (Min: 7, Max: 8)</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_optional}
                  onChange={(e) => setFormData({ ...formData, is_optional: e.target.checked })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#D4AF37' },
                  }}
                />
              }
              label="Optional Holiday (employees can choose)"
              sx={{ mb: 1, color: '#B4C4A8' }}
            />
            <Typography variant="caption" sx={{ color: '#B4C4A8', display: 'block' }}>
              {formData.is_optional
                ? "Optional holidays appear in employee selection lists for them to choose from."
                : "Mandatory holidays apply to all employees automatically."
              }
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(212,175,55,0.3)' }}>
            <Button 
              onClick={() => setShowDialog(false)} 
              sx={{ color: '#B4C4A8', '&:hover': { bgcolor: 'rgba(212,175,55,0.1)' } }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSave}
              sx={{ bgcolor: '#D4AF37', color: 'rgb(10, 25, 41)', '&:hover': { bgcolor: '#C19B2E' } }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Holiday Details Dialog */}
        <Dialog 
          open={showViewDialog} 
          onClose={() => setShowViewDialog(false)} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'rgb(10, 25, 41)',
              border: '2px solid #D4AF37',
              borderRadius: 3,
            }
          }}
        >
          <DialogTitle sx={{ color: '#D4AF37', fontWeight: 600, borderBottom: '1px solid rgba(212,175,55,0.3)' }}>
            Holiday Details
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {selectedHoliday && (
              <Box>
                <Typography variant="h5" sx={{ mb: 2, color: '#D4AF37', fontWeight: 600 }}>
                  {selectedHoliday.holiday_name}
                </Typography>
                
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                  <Box sx={{ p: 2, bgcolor: 'rgba(33, 51, 28, 0.8)', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Date</Typography>
                    <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                      {formatDate(selectedHoliday.holiday_date)}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2, bgcolor: 'rgba(33, 51, 28, 0.8)', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Day</Typography>
                    <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                      {selectedHoliday.day}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2, bgcolor: 'rgba(33, 51, 28, 0.8)', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Type</Typography>
                    <Chip 
                      size="small" 
                      label={selectedHoliday.is_optional ? 'Employee Choice' : 'Auto-applied'} 
                      sx={{
                        mt: 0.5,
                        bgcolor: selectedHoliday.is_optional ? 'rgba(255, 152, 0, 0.2)' : 'rgba(33, 150, 243, 0.2)',
                        color: selectedHoliday.is_optional ? '#FFB74D' : '#90CAF9',
                        border: `1px solid ${selectedHoliday.is_optional ? '#FFB74D' : '#90CAF9'}`
                      }} 
                    />
                  </Box>
                  <Box sx={{ p: 2, bgcolor: 'rgba(33, 51, 28, 0.8)', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Year</Typography>
                    <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                      {selectedHoliday.year}
                    </Typography>
                  </Box>
                </Box>
                
                {selectedHoliday.description && (
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(33, 51, 28, 0.8)', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Description</Typography>
                    <Typography variant="body1" sx={{ color: '#FFFFFF' }}>{selectedHoliday.description}</Typography>
                  </Box>
                )}

                {selectedHoliday.is_optional && (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2, color: '#D4AF37' }}>Employee Selections</Typography>
                    {holidaySelections.length > 0 ? (
                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300, bgcolor: '#2A3E24', border: '1px solid #D4AF37' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'rgb(10, 25, 41)' }}>
                              <TableCell sx={{ color: '#D4AF37', fontWeight: 600 }}>Employee</TableCell>
                              <TableCell sx={{ color: '#D4AF37', fontWeight: 600 }}>Status</TableCell>
                              <TableCell sx={{ color: '#D4AF37', fontWeight: 600 }}>Requested Date</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {holidaySelections.map((selection) => (
                              <TableRow key={selection.id} hover sx={{ '&:hover': { bgcolor: 'rgba(212,175,55,0.05)' } }}>
                                <TableCell>
                                  <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                                    {selection.employee.first_name} {selection.employee.last_name}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#B4C4A8' }} display="block">
                                    {selection.employee.employee_id}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={selection.status}
                                    sx={{
                                      bgcolor: selection.status === 'approved' ? 'rgba(76, 175, 80, 0.2)' : 
                                        selection.status === 'pending' ? 'rgba(255, 152, 0, 0.2)' : 
                                        'rgba(244, 67, 54, 0.2)',
                                      color: selection.status === 'approved' ? '#81C784' : 
                                        selection.status === 'pending' ? '#FFB74D' : 
                                        '#EF9A9A',
                                      border: `1px solid ${selection.status === 'approved' ? '#81C784' : 
                                        selection.status === 'pending' ? '#FFB74D' : 
                                        '#EF9A9A'}`
                                    }}
                                  />
                                </TableCell>
                                <TableCell sx={{ color: '#B4C4A8' }}>
                                  {new Date(selection.requested_date).toLocaleDateString()}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" sx={{ color: '#B4C4A8', p: 2, bgcolor: 'rgba(33, 51, 28, 0.6)', borderRadius: 2, textAlign: 'center' }}>
                        No employees have selected this holiday yet.
                      </Typography>
                    )}
                  </Box>
                )}

                {!selectedHoliday.is_optional && (
                  <Box sx={{ p: 2, bgcolor: 'rgba(33, 51, 28, 0.8)', borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1, color: '#D4AF37' }}>Holiday Coverage</Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                      This is a mandatory holiday that applies to all employees automatically.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(212,175,55,0.3)' }}>
            <Button 
              onClick={() => setShowViewDialog(false)} 
              sx={{ color: '#B4C4A8', '&:hover': { bgcolor: 'rgba(212,175,55,0.1)' } }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Holidays;